import { useCallback } from "react"
import { useMutation } from "urql"

import type { ResendToIndividualCustomerInput, ResendToIndividualCustomerMutation } from "@/graphql/codegen/graphql"
import { ResendToIndividualCustomerDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import "@/screens/Pricing/graphql/mutations"

interface UseReSendToIndividualCustomerReturn {
  resendToIndividualCustomer: (input: ResendToIndividualCustomerInput) => Promise<boolean>
  isPromoting: boolean
}

export const useReSendToIndividualCustomer = (): UseReSendToIndividualCustomerReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const [{ fetching }, executeMutation] = useMutation<
    ResendToIndividualCustomerMutation,
    { input: ResendToIndividualCustomerInput }
  >(ResendToIndividualCustomerDocument)

  const resendToIndividualCustomer = useCallback(
    async (input: ResendToIndividualCustomerInput): Promise<boolean> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.priceChange.resendToIndividualCustomer
      const hasError = error || output?.__typename === "ResendToIndividualCustomerFailure"

      if (hasError) {
        notify({ type: NotificationType.Error, message: "Unable to complete action. Please try again." })
        return false
      }
      notify({ type: NotificationType.Success, message: "Price changes resent successfully." })
      return true
    },
    [executeMutation, notify]
  )

  return { resendToIndividualCustomer, isPromoting: fetching }
}
